import React from 'react'
import { Well } from '@pushly/aqe/lib/components'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { Input, Select } from 'antd'
import type {
  IJourneySubscriberFcapRequest,
  SubscriberFcapType,
  SubscriberFcapWindow,
} from '@pushly/models/lib/structs/journeys/requests/request-interfaces'

type EntryCapSelection = SubscriberFcapType | 'no-fcap'

export interface ISubscriberEntryCapSectionProps {
  disabled: boolean
  cap: IJourneySubscriberFcapRequest | undefined
  onChange: (next: IJourneySubscriberFcapRequest | undefined) => void
}

const SubscriberEntryCapSectionCmp: React.FC<ISubscriberEntryCapSectionProps> = ({
  disabled,
  cap,
  onChange,
}) => {
  const entryCapType: EntryCapSelection = cap?.type ?? 'no-fcap'
  const showOncePerCriteria = entryCapType === 'once_per_criteria'
  const entryCapFrequency = cap?.criteria?.frequency
  const entryCapWindow = cap?.criteria?.window

  const handleTypeChange = (value: EntryCapSelection) => {
    if (disabled) return
    if (value === 'no-fcap') {
      onChange(undefined)
      return
    }

    const nextCap: IJourneySubscriberFcapRequest = { ...cap, type: value }

    if (value === 'once_per_criteria' && !cap) {
      nextCap.criteria = { frequency: 1, window: 'days' }
    }

    onChange(nextCap)
  }

  const handleFreqChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return
    const raw = e?.target?.value
    const parsed = raw === '' ? undefined : Number.parseInt(raw, 10)
    const freq = parsed !== undefined && !Number.isNaN(parsed) ? parsed : undefined
    onChange({
      type: cap?.type ?? 'once_per_criteria',
      criteria: {
        frequency: freq as number,
        window: cap?.criteria?.window ?? 'days',
      },
    })
  }

  const handleWindowChange = (value: SubscriberFcapWindow) => {
    if (disabled) return
    onChange({
      type: cap?.type ?? 'once_per_criteria',
      criteria: {
        frequency: cap?.criteria?.frequency ?? 1,
        window: value,
      },
    })
  }

  return (
    <Well mode="ghost" title="Entry Frequency Cap" hideFooter={true} className="entry-cap-well">
      <Form.Item className="journey-trigger-editor-label" label="Subscribers not currently in this journey">
        <Select
          className="journey-trigger-editor-rec-frequency-select"
          size="middle"
          disabled={disabled}
          value={entryCapType}
          onChange={handleTypeChange}
        >
          <Select.Option value="no-fcap">may enter this journey every time it runs</Select.Option>
          <Select.Option value="once">may only enter this journey once in their lifetime</Select.Option>
          <Select.Option value="once_per_criteria">may only enter this journey once every</Select.Option>
        </Select>
        {showOncePerCriteria && (
          <div className="rcte-fcap-criteria-builder">
            <div className="rcte-fcap-frequency-wrapper">
              <Input
                className="rcte-fcap-frequency"
                size="small"
                disabled={disabled}
                defaultValue={entryCapFrequency}
                value={entryCapFrequency}
                onChange={handleFreqChange}
                addonAfter={
                  <Select
                    className="rcte-fcap-window-select"
                    disabled={disabled}
                    defaultValue={entryCapWindow}
                    value={entryCapWindow}
                    onChange={handleWindowChange}
                  >
                    <Select.Option value="days">day(s)</Select.Option>
                    <Select.Option value="months">month(s)</Select.Option>
                  </Select>
                }
              />
            </div>
          </div>
        )}
      </Form.Item>

    </Well>
  )
}

export default React.memo(SubscriberEntryCapSectionCmp)
